import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useViewerStatsStore } from '@/modules/viewer/store/useViewerStatsStore';
import { useAiMetadataStore } from './modules/viewer/store/useAiMetadataStore';
import { useArtifactsStore } from './modules/viewer/store/useArtifactsStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { useUserStore } from '@/store/useUserStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useSubscriptionStore } from '@/store/useSubscriptionStore';
import { usePlansStore } from '@/store/usePlansStore';
import { Feature, useFeaturesStore } from './store/useFeaturesStore';
import { useEmbedStore } from '@/modules/viewer/store/useEmbedStore';
import { camelCase, snakeCase } from '@replygirl/change-case-object';
import { useRedactionStore, RedactionSummary } from '@/modules/viewer/store/useRedactionStore';
import { useSmartActionsStore } from './modules/viewer/store/useSmartActionsStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';

export interface StoreInitializerData {
  user: object;
  viewer: any;
  settings: object;
  organization: object;
  subscription: object;
  plans: object;
  features: object;
  stats: object;
  oembedService: string;
  entitlements: { capabilities: Record<string, boolean>; limits: Record<string, number | null>; quotas: Record<string, any>; meta: Record<string, any> };
}

// @todo cleanup serializer data - flatten structure for viewer data
export class StoreInitializer {
  public init(data: Partial<StoreInitializerData>) {
    if (data.user) this.initializeUser(data.user);
    if (data.viewer) {
      this.initializeViewer(data.viewer);
      this.initializeStats(data.viewer);
      this.initializeTranscription(data.viewer);
      this.initializeAiMetadata(data.viewer.itemAiMetadata);
      this.initializeItemArtifacts(data.viewer.itemArtifacts);
      this.initializeSmartActions(data.viewer.smartActions);
      this.initializeRedaction(data.viewer.redaction, data.viewer.redactionAvailable);
    }

    if (data.settings) this.initializeSettings(data.settings);
    if (data.organization) this.initializeOrganization(data.organization);
    if (data.subscription) this.initializeSubscription(data.subscription);
    if (data.plans) this.initializePlans(data.plans);
    if (data.features) this.initializeFeatures(snakeCase(data.features));
    if (data.viewer && data.viewer.itemFeatures) this.initializeViewerItemFeatures(snakeCase(data.viewer.itemFeatures));
    if (data.oembedService) this.initializeEmbedService(data.oembedService);
    if (data.entitlements) this.initializeEntitlements(data.entitlements);
  }

  private initializeUser(userData: object) {
    const store = useUserStore();
    store.initialize(userData);
  }

  private initializeOrganization(orgData: object) {
    const store = useOrganizationStore();
    store.initialize(orgData);
  }

  private initializeSubscription(subscriptionData: object) {
    const store = useSubscriptionStore();
    store.initialize(subscriptionData);
  }

  private initializeViewer(viewerData: object) {
    const store = useViewerItemStore();
    store.initialize(viewerData);
  }

  private initializeStats(statsData: object) {
    const store = useViewerStatsStore();
    store.initialize(statsData);
  }

  private initializeAiMetadata(statsData: object) {
    const store = useAiMetadataStore();
    // filler_words/silences (AI Playback, SW-6793) arrive snake_case from the
    // server-rendered @store_data — unlike API-fetched updates to this store,
    // which pass through the axios client's automatic camelCase conversion.
    store.initialize(camelCase(statsData));
  }

  private initializeItemArtifacts(statsData: any[]) {
    const store = useArtifactsStore();
    store.initialize(statsData);
  }

  private initializeRedaction(redactionData: RedactionSummary | null, isAvailable: boolean) {
    const store = useRedactionStore();
    // `counts` is keyed by category id, and `aws_key`/`secret_key` carry underscores:
    // without this, a page load and a pusher update disagree on those keys.
    store.initialize(redactionData ? (camelCase(redactionData) as RedactionSummary) : null, isAvailable);
  }

  private initializeSmartActions(smartActionsData: object) {
    const store = useSmartActionsStore();
    store.initialize(smartActionsData);
  }

  private initializeTranscription(statsData: object) {
    const store = useTranscriptionStore();
    store.initialize(statsData);
  }

  private initializeSettings(settingsData: object) {
    const store = useSettingsStore();
    store.initialize(settingsData);
  }

  private initializePlans(plansData: object) {
    const store = usePlansStore();
    store.initialize(plansData);
  }

  private initializeFeatures(data: object) {
    const store = useFeaturesStore();
    store.initialize(data as unknown as Feature[]);
  }

  private initializeViewerItemFeatures(data: object) {
    const store = useFeaturesStore();
    store.initializeItem(data as unknown as Feature[]);
  }

  private initializeEmbedService(url: string) {
    const store = useEmbedStore();
    store.initialize(url);
  }

  private initializeEntitlements(data: StoreInitializerData['entitlements']) {
    const store = useEntitlementsStore();
    store.$patch({
      capabilities: camelCase(data.capabilities),
      limits: camelCase(data.limits),
      quotas: camelCase(data.quotas),
      isLoaded: true,
    });
  }
}
